import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './ui/input';
import { API } from '../utils/config';
import { MapPin, Loader2, Check } from 'lucide-react';

/**
 * Componente Autocomplete per la selezione dei comuni italiani
 * Usa i codici ufficiali della Polizia di Stato per Alloggiati Web
 * 
 * Props:
 * - value: valore corrente del campo
 * - onChange: callback quando il valore cambia (passa nome comune)
 * - onSelect: callback quando un comune viene selezionato (passa oggetto {codice, nome, provincia})
 * - placeholder: placeholder dell'input
 * - className: classi CSS aggiuntive
 * - disabled: disabilita l'input
 * - required: campo obbligatorio
 * - id: id dell'input
 */
const ComuneAutocomplete = ({
  value = '',
  onChange,
  onSelect,
  placeholder = 'Cerca comune...',
  className = '',
  disabled = false,
  required = false,
  id,
  error = false,
  allowCustom = false
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selected, setSelected] = useState(false);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customProvincia, setCustomProvincia] = useState('');
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Sincronizza input con value prop
  useEffect(() => {
    setInputValue(value);
    if (value) setSelected(true);
  }, [value]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions con debounce
  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/api/questura/comuni/search?q=${encodeURIComponent(query)}&limit=15`);
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.results || []);
      }
    } catch (error) {
      console.error('Errore ricerca comuni:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSelected(false);
    setSelectedIndex(-1);
    setShowSuggestions(true);
    
    if (onChange) {
      onChange(newValue);
    }

    // Debounce API call
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  // Handle suggestion selection
  const handleSelect = (suggestion) => {
    setInputValue(suggestion.nome);
    setSelected(true);
    setShowSuggestions(false);
    setSuggestions([]);
    
    if (onChange) {
      onChange(suggestion.nome);
    }
    if (onSelect) {
      onSelect(suggestion);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          id={id}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => inputValue.length >= 2 && setShowSuggestions(true)}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={`pl-10 pr-10 ${error ? 'border-red-500' : ''} ${className}`}
          autoComplete="off"
          data-testid="comune-autocomplete-input"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          {!loading && selected && <Check className="h-4 w-4 text-green-500" />}
        </div>
      </div>

      {/* Dropdown suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <div 
          className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          data-testid="comune-suggestions"
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.codice}
              onClick={() => handleSelect(suggestion)}
              className={`px-4 py-2 cursor-pointer flex items-center justify-between
                ${index === selectedIndex ? 'bg-blue-50' : 'hover:bg-slate-50'}
                ${index !== suggestions.length - 1 ? 'border-b border-slate-100' : ''}
              `}
              data-testid={`comune-option-${index}`}
            >
              <div>
                <div className="font-medium text-slate-800">{suggestion.nome}</div>
                <div className="text-xs text-slate-500">{suggestion.provincia}</div>
              </div>
              <div className="text-xs font-mono text-slate-400">
                {suggestion.codice}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No results message - with option to add custom */}
      {showSuggestions && inputValue.length >= 2 && suggestions.length === 0 && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg">
          <div className="p-4 text-center text-slate-500 text-sm">
            Nessun comune trovato per "{inputValue}"
          </div>
          {allowCustom && !showCustomForm && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); setShowCustomForm(true); }}
              className="w-full px-4 py-2 text-sm text-green-700 hover:bg-green-50 border-t border-slate-200 flex items-center gap-2"
              data-testid="add-custom-comune-btn"
            >
              <MapPin className="h-4 w-4" />
              Aggiungi "{inputValue}" manualmente
            </button>
          )}
          {allowCustom && showCustomForm && (
            <div className="p-3 border-t border-slate-200 space-y-2" data-testid="custom-comune-form">
              <div className="text-xs font-medium text-slate-600">Aggiungi comune personalizzato</div>
              <input
                type="text"
                value={customProvincia}
                onChange={(e) => setCustomProvincia(e.target.value.toUpperCase())}
                placeholder="Sigla provincia (es: GE, MI)"
                maxLength={2}
                className="w-full text-sm border rounded px-3 py-1.5 uppercase focus:ring-1 focus:ring-blue-400 focus:outline-none"
                data-testid="custom-comune-provincia"
              />
              <button
                type="button"
                onClick={async (e) => {
                  e.preventDefault();
                  if (!customProvincia || customProvincia.length !== 2) return;
                  const comuneObj = {
                    codice: `CUSTOM_${inputValue.toUpperCase().replace(/\s/g, '_')}`,
                    nome: inputValue.toUpperCase(),
                    provincia: customProvincia.toUpperCase()
                  };
                  // Save to backend
                  try {
                    await fetch(`${API}/api/istat/comuni`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                      body: JSON.stringify({ nome: inputValue, provincia: customProvincia })
                    });
                  } catch (err) { /* continue anyway */ }
                  setInputValue(comuneObj.nome);
                  setSelected(true);
                  setShowSuggestions(false);
                  setShowCustomForm(false);
                  setCustomProvincia('');
                  if (onChange) onChange(comuneObj.nome);
                  if (onSelect) onSelect(comuneObj);
                }}
                disabled={customProvincia.length !== 2}
                className="w-full text-sm bg-green-600 text-white rounded px-3 py-1.5 hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="custom-comune-save"
              >
                Conferma "{inputValue}" ({customProvincia || '??'})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ComuneAutocomplete;
